import { motion, useMotionValue, useTransform, AnimatePresence, animate } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import DOMPurify from 'dompurify';

function replacePlaceholders(html, replacements) {
  return html.replace(/{{(.*?)}}/g, (_, key) => replacements[key.trim()] || '');
}


export default function SwipeCard({ email, onSwipe, forcedDirection, receiverEmail, innerRef }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-20, 20]);
  const [visible, setVisible] = useState(true);

  const handleSwipeComplete = useCallback((dir) => {
    const userAnswer = dir === 'left';
    const isCorrect = userAnswer === email.isPhishing;

    if (isCorrect) {
      confetti({
        particleCount: 60,
        spread: 70,
        origin: { y: 0.6 }
      });
    }

    onSwipe(dir, email);
    setVisible(false);
  }, [email, onSwipe]);


  const handleDragEnd = (_, info) => {
    if (info.offset.x > 150) handleSwipeComplete('right');
    else if (info.offset.x < -150) handleSwipeComplete('left');
    else x.set(0);
  };

  useEffect(() => {
    if (!forcedDirection) return;

    const offset = forcedDirection === 'right' ? 300 : -300;
    const direction = forcedDirection;

    x.stop();
    animate(x, offset, {
      type: 'spring',
      stiffness: 150,
      damping: 20,
      onComplete: () => handleSwipeComplete(direction),
    });
  }, [forcedDirection, x, handleSwipeComplete]);


  const nickname = localStorage.getItem('nickname') || 'gebruiker';
  const interpolatedBody = replacePlaceholders(email.body, { nickname });

  const cleanBody = DOMPurify.sanitize(interpolatedBody, {
    ALLOWED_ATTR: ['class', 'href', 'style', 'src', 'title'],
  });


  const cleanFooter = DOMPurify.sanitize(email.footer, {
    ALLOWED_ATTR: ['class', 'href', 'style'],
  });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={innerRef}
          className="SwipeCard bg-[#e2e8f0] text-[#2b3b4b] p-6 rounded-xl shadow-xl max-w-[640px] w-full sm:w-[90%] md:w-[80%] lg:w-[640px]"
          style={{ x, rotate }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={handleDragEnd}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
        >
          <div>
            <h3 className="pb-4 text-xl font-semibold">{email.subject}</h3>

            <div className="pb-4 text-sm text-gray-600 space-y-1">
              <div className="flex flex-wrap items-center text-sm text-gray-600">
                <strong className="mr-1">From:</strong>
                {email.department && <span className="mr-2">{email.department}</span>}
                <span className="relative group inline-block">
                  <a
                    href={`mailto:${email.sender}`}
                    onClick={(e) => {
                      const target = e.target;
                      if (target.tagName === 'A') {
                        e.preventDefault();
                      }
                    }}
                    className="text-gray-500 hover:underline hover:text-[#00aca9]"
                  >
                    {email.sender}
                  </a>
                  <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-black text-white text-xs font-mono px-2 py-1 rounded shadow-lg z-10">
                    {email.sender}
                  </div>
                </span>
              </div>

              <p>
                <strong>To:</strong> {receiverEmail}
              </p>
            </div>

            <hr className="border-gray-300 w-full" />

            <div className="py-4">
              <div
                className="text-sm leading-relaxed"
                onClick={(e) => {
                  const target = e.target;
                  if (target.tagName === 'A') {
                    e.preventDefault();
                  }
                }}
                dangerouslySetInnerHTML={{ __html: cleanBody }}
              />

            </div>

            {cleanFooter.trim() && (
              <>
                <hr className="border-gray-300 w-full" />
                <div
                  className="text-xs text-gray-500 text-center pt-2"
                  onClick={(e) => {
                    const target = e.target;
                    if (target.tagName === 'A') {
                      e.preventDefault();
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: cleanFooter }}
                />
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
